import {useEffect, useRef} from 'react';

import {useSharedConfig, WOFSlice} from '@src/Components/SharedConfigProvider';
import {useAnimationFrame} from '@src/useAnimationFrame';

import SingleWOFSlice from './SingleWOFSlice';
import * as classes from './WOFComponent.module.scss';

interface Props {
	isSpinning: boolean;
	onWin?: (wofSlice: WOFSlice) => void;
}

const wofSlices: WOFSlice[] = [
	WOFSlice.Prize1,
	WOFSlice.NoPrize,
	WOFSlice.Prize2,
	WOFSlice.NoPrize,
	WOFSlice.Prize3,
	WOFSlice.NoPrize,
	WOFSlice.Prize2,
	WOFSlice.NoPrize,
	WOFSlice.Prize3,
	WOFSlice.NoPrize,
];

enum EAnimationState {
	Stopped,
	RampingUp,
	Running,
	SlowingDown,
}

const WOFComponent = (props: Props) => {
	const {randomWin} = useSharedConfig();

	const wheelRef = useRef<HTMLDivElement>(null);
	const wasSpinning = useRef<boolean>(false);

	const currentRotation = useRef(0);
	const wheelSpeed = useRef(0);
	const animationState = useRef(EAnimationState.Stopped);
	const targetRotation = useRef(0);
	const winningType = useRef(WOFSlice.NoPrize);

	const fullWheelSpeed = 15; // lower = slower
	const rampUpFactor = 30; // lower = faster
	const rampDownFactor = 50; // lower = faster

	// Detect Spinning Prop change
	useEffect(() => {
		if (props.isSpinning !== wasSpinning.current) {
			wasSpinning.current = props.isSpinning;

			if (props.isSpinning) {
				console.log('Ramping Up');
				// Reset Variables
				wheelSpeed.current = 0;
				targetRotation.current = 0;
				animationState.current = EAnimationState.RampingUp;
			} else {
				console.log('Slowing Down');

				// Get Random Winning Slice index
				winningType.current = randomWin();
				const possibleWinningIndices = wofSlices
					.map((slice, index) =>
						slice === winningType.current ? index : -1
					)
					.filter((index) => index !== -1);
				const randomIndex = Math.floor(
					Math.random() * possibleWinningIndices.length
				);
				const winningIndex = possibleWinningIndices[randomIndex];
				console.log(
					`WINNING INDEX: ${winningIndex.toString()} (type: ${winningType.current.toString()})`
				);

				// Calculate end position based on target and current speed
				const sliceAngle = 360 / wofSlices.length;
				const endPosition =
					90 + sliceAngle * (1 + wofSlices.length - winningIndex);

				const extraRevolutions = Math.floor(
					(wheelSpeed.current / fullWheelSpeed) * 2
				);

				const randomOffset =
					(sliceAngle * Math.random() - sliceAngle / 2) * 0.75;

				targetRotation.current =
					currentRotation.current +
					extraRevolutions * 360 +
					randomOffset +
					endPosition -
					(currentRotation.current % 360);

				console.log(
					`Winning Position: ${endPosition.toString()}, Rotation: ${targetRotation.current.toString()} (${(targetRotation.current % 360).toString()}deg and ${Math.floor(targetRotation.current / 360).toString()})`
				);
				animationState.current = EAnimationState.SlowingDown;
			}
		}
	}, [props.isSpinning, randomWin]);

	// Main Animation Logic
	useAnimationFrame((_deltaTime: number) => {
		if (animationState.current === EAnimationState.Stopped) {
			// Nothing to do
			return;
		}

		// Check if we need to increase speed
		if (animationState.current === EAnimationState.RampingUp) {
			wheelSpeed.current +=
				(fullWheelSpeed - wheelSpeed.current) / rampUpFactor;

			if (wheelSpeed.current >= fullWheelSpeed - 1) {
				wheelSpeed.current = fullWheelSpeed;
				animationState.current = EAnimationState.Running;
				console.log('reached full speed');
			}
		}

		// Animate Wheel (Running and Ramping Up)
		if (wheelRef.current) {
			let newRotation;

			// Slow down, switch to target position animation style
			if (animationState.current === EAnimationState.SlowingDown) {
				newRotation =
					currentRotation.current +
					(targetRotation.current - currentRotation.current) /
						rampDownFactor;

				if (targetRotation.current - currentRotation.current <= 2) {
					animationState.current = EAnimationState.Stopped;
					console.log('wheel stopped');

					// inform about win
					props.onWin?.(winningType.current);
				}
			} else {
				newRotation = currentRotation.current + wheelSpeed.current;
			}

			currentRotation.current = newRotation;
			wheelRef.current.style.rotate = `${newRotation.toString()}deg`;
		}
	});

	return (
		<div className={classes.wheelContainer}>
			<div className={classes.wheel} ref={wheelRef}>
				{wofSlices.map((sliceType, index) => (
					<SingleWOFSlice
						key={index}
						type={sliceType}
						sliceIndex={index}
						totalSlices={wofSlices.length}
					/>
				))}
			</div>
		</div>
	);
};

export default WOFComponent;
